<template>
	<section class="accordion">
		<ul @mouseleave="onMouseLeave">
			<li v-for="(item, n) in items" :key="`accordion_${n}`" :class="{ var_hover: hover == n }">
				<span
					class="title"
					@mouseenter="onPointerMouseEnter($event)"
					@mouseleave="onPointerMouseLeave($event)"
					@click="setCurrent(n)"
				>
					{{ item.title }}
					<span class="arrow" :class="{ active: n == current }">
						<span></span>
						<span></span>
						<span></span>
					</span>
					<!-- <span class="pulse"></span> -->
				</span>
				<transition @enter="onEnter" @leave="onLeave">
					<div class="content_wrapper" v-show="n == current">
						<div class="content" v-html="item.description"></div>
						<ModuleDownload v-if="item.files" :files="item.files"></ModuleDownload>
					</div>
				</transition>
			</li>
		</ul>
	</section>
</template>

<script>
import gsap from "gsap";
import EventBus from "@/helpers/event-bus";
import ModuleDownload from "@/components/modules/ModuleDownload";

export default {
	props: {
		items: [Boolean, Array],
	},
	components: {
		ModuleDownload,
	},
	data() {
		return {
			current: -1,
			hover: -1,
		};
	},
	methods: {
		onPointerMouseEnter(ev, type = "default") {
			const parent = ev.currentTarget.getElementsByClassName("arrow")[0];
			EventBus.$emit(EventBus.pointer.enter, { el: parent, type, parent });
		},
		onPointerMouseLeave(ev, type = "default") {
			const parent = ev.currentTarget.getElementsByClassName("arrow")[0];
			EventBus.$emit(EventBus.pointer.leave, { el: parent, type, parent });
		},
		setCurrent(n) {
			this.current = this.current == n ? -1 : n;
		},
		onEnter(el, done) {
			gsap.from(el, {
				duration: 0.5,
				height: 0,
				opacity: 0,
				onComplete: () => {
					gsap.set(el, { clearProps: "height,opacity" });
					done();
				},
			});
		},
		onMouseLeave() {
			this.hover = -1;
		},
		onLeave(el, done) {
			gsap.to(el, {
				duration: 0.5,
				height: 0,
				opacity: 0,
				onComplete: () => {
					gsap.set(el, { clearProps: "height,opacity" });
					done();
				},
			});
		},
		onHover(ev, n) {
			this.hover = n;
			console.log(this.hover);
		},
	},
};
</script>

<style lang="scss" scoped>
@keyframes pulsing {
	0% {
		transform: scale(1);
		opacity: 0.3;
	}

	20% {
		opacity: 0.3;
	}

	100% {
		transform: scale(2.2);
		opacity: 0;
	}
}

@keyframes border1 {
	0% {
		border-radius: 50% 70% 60% 45% / 60% 55% 60% 55%;
	}

	25% {
		border-radius: 100% 55% 70% 90% / 90% 100% 60% 80%;
	}

	50% {
		border-radius: 60% 55% 60% 55% / 50% 70% 60% 45%;
	}

	75% {
		border-radius: 100% 50% 50% 50% / 115% 60% 80% 44%;
	}

	100% {
		border-radius: 50% 70% 60% 45% / 60% 55% 60% 55%;
	}
}

.accordion {
	@include moduleSpacing;
	@include fontCopy;
	@include textFormat;

	border-bottom: 2px solid rgba($colorRed2, 0.2);
	width: 100%;

	@include mq($from: m) {
		@include gridColumn(m, 16, 1, false, true);
	}

	@include mq($from: xl) {
		@include gridColumn(xl, 15, 2, false, true);
	}

	& > ul {
		margin: 0;
		padding: 0;
		list-style: none;

		li {
			margin: 0;
			padding: 0;
			border-top: 2px solid rgba($colorRed2, 0.2);

			&::v-deep ol {
				li {
					list-style: decimal;

					&:before {
						display: none;
					}
				}
			}

			&.var_hover {
				.arrow {
					animation-play-state: running;
				}
			}
		}

		li:before {
			display: none;
		}
	}

	.title {
		@include fontCopy;
		position: relative;
		font-weight: 400;
		display: block;
		padding: 30px 60px 30px 0;
		cursor: pointer;
	}

	.content_wrapper {
		display: block;
		overflow: hidden;
	}

	.download {
		margin-top: 20px;

		&/deep/ .files {
			margin: 0;
			width: 100%;

			@include mq($to: m) {
				transform: none;
				padding: 0;
			}

			li {
				margin: 0;

				@include mq($to: m) {
					max-width: 50%;
					padding: 15px 10px;
				}
			}

			li::before {
				display: none;
			}
		}
	}

	.content {
		display: block;
		padding: 0 0 25px 0;

		@include mq($from: m) {
			padding: 0 0 40px 0;
		}

		@include mq($from: xl) {
			padding: 0 0 40px 0;
			@include gridColumn(xl, 12, 1, 15, true);
		}

		&::v-deep p:first-child {
			margin-top: 0;
		}

		&::v-deep p {
			a {
				transition: color 0.3s;

				&:hover {
					color: $colorRed1 !important;
				}
			}
		}
	}

	.arrow {
		position: absolute;
		right: 0;
		top: calc(50% - 19px);

		display: block;
		width: 41px;
		height: 38px;
		border-radius: 50% 70% 60% 45% / 60% 55% 60% 55%;
		background-color: $colorRed1;
		z-index: 10;
		animation: border1 4s infinite linear;
		animation-play-state: paused;
		transition: background 0.7s;

		&.active {
			background-color: #fff;
			transition: background-color 0.5s;

			& span {
				background-color: #000;

				&:nth-child(1) {
					transform: translate(-50%, -50%) rotate(90deg) scale(0, 1);
				}

				&:nth-child(2) {
					transform: translate(-100%, -50%) rotate(0deg) scale(1.5, 1);
				}
			}
		}

		& span {
			position: absolute;
			top: 50%;
			left: 50%;
			transform: translate(-50%, -50%) rotate(90deg);
			width: 18px;
			height: 2px;
			background-color: #000;
			transition: transform 0.3s, background-color 0.4s ease 0.2s;
			border-radius: 1px;

			&:nth-child(2) {
				transform-origin: left center;
				width: 9px;
				transform: translate(-9px, -50%) rotate(0deg);
			}

			&:nth-child(3) {
				width: 9px;
				transform-origin: right center;
				transform: translate(0, -50%) rotate(0deg);
			}
		}
	}

	.pulse {
		display: block;
		position: absolute;
		right: 0;
		top: calc(50% - 19px);
		width: 41px;
		height: 38px;

		&::before,
		&::after {
			content: "";
			position: absolute;
			height: 100%;
			width: 100%;
			background-color: $colorRed1;
			border-radius: 50% 70% 60% 45% / 60% 55% 60% 55%;
			opacity: 0.2;
		}
	}
}
</style>
